/*
================================================================================
FILE HEADER — apps/studio-web/src/components/AssistBar.tsx
================================================================================

CHANGE LOG
----------
| #  | Date       | Who               | Why                    | What Changed          |
|----|------------|-------------------|------------------------|-----------------------|
| 1  | 2026-07-02 | Claude Sonnet 5   | Phase 6 AI Assist mode | Created — command bar |
|    |            |                   |                        | UI: ghost text, parse |
|    |            |                   |                        | + resolve loop, live  |
|    |            |                   |                        | autocomplete, preview |
|    |            |                   |                        | chip, Pick Element    |
| 2  | 2026-07-03 | Claude Sonnet 5   | Bug fixes from live    | commit() only handled |
|    |            |                   | Chrome+Google session  | state.kind==='ready'  |
|    |            |                   |                        | so Pick Element from  |
|    |            |                   |                        | 'no-match' silently   |
|    |            |                   |                        | no-op'd — unified all |
|    |            |                   |                        | paths through          |
|    |            |                   |                        | commitWith(). Added    |
|    |            |                   |                        | pickError feedback and |
|    |            |                   |                        | a 'reason' field so    |
|    |            |                   |                        | "model failed to load" |
|    |            |                   |                        | / "tree unavailable" / |
|    |            |                   |                        | "no such element" show |
|    |            |                   |                        | distinct messages      |

FILES THAT IMPORT THIS FILE
-----------------------------
- apps/studio-web/src/components/RecorderPanel.tsx

EXPORTS COVERED
---------------
- AssistBar — the ✨ Assist mode command bar + preview/confirm UI

CALLER -> CALLEE MAP
---------------------
- onInputChange (debounced 250ms) -> parseAssistSentence() [Lane 0, sync]
                                   -> onRequestTree() + resolveTarget() [Lane 1, async]
- Confirm click / Enter key       -> buildAssistSteps() -> onAddSteps()
- Pick Element click              -> onRequestCapture() -> merges into buildAssistSteps()

IMPORTANT POINTS
----------------
1. One sentence = one step (or two, for "type X into Y" -> [tap, typeText]).
   This is NOT a chat interface — Enter commits and clears the bar.
2. Never auto-picks under ambiguity: renders all top candidates as clickable
   rows and requires an explicit click before Confirm is enabled.
3. Autocomplete only fires once a verb + role/target boundary is plausible
   (heuristic: parse must already be `ok` with a role or target set) — this
   avoids spamming CAPTURE_TREE requests while the user is still typing the
   verb itself.
4. Ghost-text rotates through GRAMMAR_EXAMPLES every 3.5s while the input is
   empty — this is the "teach the DSL through the UI, never an error dialog"
   principle from AI_ASSIST_Research_Plan.md §7.
5. A failed parse is never a bare error: shows the 3 closest supported
   patterns as clickable chips that fill the input when clicked.
================================================================================
*/

import { useState, useEffect, useRef, useCallback } from 'react';
import type { IRStep, Selector } from '@plex/ir';
import type { SerializedAXTree } from '../extension/protocol.js';
import { parseAssistSentence, GRAMMAR_EXAMPLES, type AssistIntent } from '../lib/assist/grammar.js';
import { resolveTarget, flattenNodes, accessibleName, type ResolvedMatch, type ResolveResult } from '../lib/assist/resolver.js';
import { buildAssistSteps } from '../lib/assist/buildStep.js';

type ParseState =
  | { kind: 'idle' }
  | { kind: 'error'; suggestions: string[] }
  | { kind: 'resolving'; intent: AssistIntent }
  // `reason` distinguishes "the local ONNX model failed to load" (model-unavailable —
  // usually a one-time network fetch issue) from "genuinely no such element on the
  // page" (no-match) from "couldn't read the page at all" (tree-unavailable) —
  // three very different problems that all used to collapse into one bare message.
  | { kind: 'no-match'; intent: AssistIntent; tree: SerializedAXTree | null; reason: 'tree-unavailable' | 'model-unavailable' | 'no-match' }
  | { kind: 'ambiguous'; intent: AssistIntent; matches: ResolvedMatch[]; tree: SerializedAXTree }
  | { kind: 'ready'; intent: AssistIntent; resolved: ResolvedMatch | null; tree: SerializedAXTree | null };

// Intents that never need element resolution — go straight to 'ready'.
const NO_RESOLUTION_KINDS = new Set<AssistIntent['kind']>([
  'openUrl', 'wait', 'screenshot', 'assertPageContainsText',
]);

type Props = {
  activeTabId: number;
  onRequestTree: () => Promise<SerializedAXTree | null>;
  onRequestCapture: (callback: (sel: Selector | null) => void) => void;
  onAddSteps: (steps: IRStep[]) => void;
};

export function AssistBar({ activeTabId, onRequestTree, onRequestCapture, onAddSteps }: Props) {
  const [input, setInput] = useState('');
  const [state, setState] = useState<ParseState>({ kind: 'idle' });
  const [ghostIdx, setGhostIdx] = useState(0);
  const [flash, setFlash] = useState<string | null>(null);
  const [autocomplete, setAutocomplete] = useState<string[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestSeq = useRef(0);

  // Rotate ghost-text examples while the bar is empty.
  useEffect(() => {
    if (input) return;
    const t = setInterval(() => setGhostIdx(i => (i + 1) % GRAMMAR_EXAMPLES.length), 3500);
    return () => clearInterval(t);
  }, [input]);

  // Clear the success flash after a beat.
  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 1800);
    return () => clearTimeout(t);
  }, [flash]);

  const runParseAndResolve = useCallback(async (sentence: string) => {
    const mySeq = ++requestSeq.current;
    const trimmed = sentence.trim();
    if (!trimmed) { setState({ kind: 'idle' }); setAutocomplete([]); return; }

    const result = parseAssistSentence(trimmed);
    if (!result.ok) {
      setState({ kind: 'error', suggestions: result.suggestions });
      setAutocomplete([]);
      return;
    }

    const { intent } = result;

    if (NO_RESOLUTION_KINDS.has(intent.kind) || (intent.kind === 'typeText' && !intent.target)) {
      setState({ kind: 'ready', intent, resolved: null, tree: null });
      setAutocomplete([]);
      return;
    }

    if (!intent.target) {
      // Parsed a kind that needs a target, but none was extracted yet (still typing).
      setState({ kind: 'idle' });
      return;
    }

    setState({ kind: 'resolving', intent });

    const tree = await onRequestTree();
    if (mySeq !== requestSeq.current) return; // superseded by a newer keystroke

    if (!tree) {
      setState({ kind: 'no-match', intent, tree: null, reason: 'tree-unavailable' });
      return;
    }

    // Live autocomplete: element names from the current tree matching the role filter.
    const all = flattenNodes(tree);
    const pool = intent.role ? all.filter(n => n.node.role === intent.role) : all;
    setAutocomplete((pool.length > 0 ? pool : all).slice(0, 8).map(n => accessibleName(n.node)).filter(Boolean));

    const resolveResult: ResolveResult = await resolveTarget(tree, intent.target, intent.role);
    if (mySeq !== requestSeq.current) return;

    if (resolveResult.matches.length === 0) {
      setState({ kind: 'no-match', intent, tree, reason: resolveResult.reason ?? 'no-match' });
    } else if (resolveResult.ambiguous) {
      setState({ kind: 'ambiguous', intent, matches: resolveResult.matches, tree });
    } else {
      setState({ kind: 'ready', intent, resolved: resolveResult.matches[0]!, tree });
    }
  }, [onRequestTree]);

  const handleInputChange = (value: string) => {
    setInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void runParseAndResolve(value), 250);
  };

  // Single commit path used by every state — 'ready', 'ambiguous' (after the
  // user picks a candidate), and 'no-match' (Pick Element as the sole source)
  // all funnel through here. Previously `commit()` only handled `state.kind
  // === 'ready'`, so confirming from 'no-match' via Pick Element silently did
  // nothing — this is the fix for that bug.
  const commitWith = useCallback((
    intent: AssistIntent,
    resolved: ResolvedMatch | null,
    tree: SerializedAXTree | null,
    manualSelector?: Selector | null,
  ): boolean => {
    const steps = buildAssistSteps(intent, resolved, tree, manualSelector);
    if (!steps) return false;
    onAddSteps(steps);
    setFlash(`✓ Added ${steps.length} step${steps.length > 1 ? 's' : ''}`);
    setInput('');
    setState({ kind: 'idle' });
    setAutocomplete([]);
    setPickError(null);
    return true;
  }, [onAddSteps]);

  const commit = useCallback((manualSelector?: Selector | null) => {
    if (state.kind === 'ready') commitWith(state.intent, state.resolved, state.tree, manualSelector);
  }, [state, commitWith]);

  const commitAmbiguousChoice = useCallback((match: ResolvedMatch, manualSelector?: Selector | null) => {
    if (state.kind !== 'ambiguous') return;
    commitWith(state.intent, match, state.tree, manualSelector);
  }, [state, commitWith]);

  const [pickError, setPickError] = useState<string | null>(null);

  const pickElement = useCallback((onDone: (sel: Selector | null) => void) => {
    setPickError(null);
    onRequestCapture(onDone);
  }, [onRequestCapture]);

  const handlePickForReady = () => {
    pickElement((sel) => {
      if (sel) commit(sel);
      else setPickError('No element found at that position — click directly on an element.');
    });
  };

  const handlePickForNoMatch = () => {
    if (state.kind !== 'no-match') return;
    const { intent, tree } = state;
    pickElement((sel) => {
      if (sel) commitWith(intent, null, tree, sel); // manual pick is the sole candidate source
      else setPickError('No element found at that position — click directly on an element.');
    });
  };

  const handlePickForAmbiguous = (match: ResolvedMatch) => {
    pickElement((sel) => {
      if (sel) commitAmbiguousChoice(match, sel);
      else setPickError('No element found at that position — click directly on an element.');
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (state.kind === 'ready') commit();
    } else if (e.key === 'Escape') {
      setInput('');
      setState({ kind: 'idle' });
      setAutocomplete([]);
      setPickError(null);
    }
  };

  const applySuggestion = (example: string) => {
    setInput(example);
    void runParseAndResolve(example);
  };

  const disabled = activeTabId < 0;

  return (
    <div style={{ borderTop: '1px solid #27272a', background: 'rgba(124,58,237,0.04)', flexShrink: 0 }}>
      <div style={{ padding: '10px 14px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ fontSize: 13, color: '#a78bfa', flexShrink: 0 }}>✨</span>
        <input
          type="text"
          value={input}
          onChange={e => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={input ? '' : GRAMMAR_EXAMPLES[ghostIdx]}
          style={{
            flex: 1, background: '#09090b', border: '1px solid #3f3f46', borderRadius: 6,
            padding: '7px 10px', color: '#e4e4e7', fontSize: 12.5, outline: 'none',
            opacity: disabled ? 0.5 : 1,
          }}
        />
        <button
          onClick={() => commit()}
          disabled={state.kind !== 'ready' || disabled}
          style={{
            background: state.kind === 'ready' ? '#7c3aed' : '#27272a', border: 'none', borderRadius: 5,
            padding: '6px 12px', color: state.kind === 'ready' ? '#fff' : '#52525b', fontSize: 12, fontWeight: 600,
            cursor: state.kind === 'ready' ? 'pointer' : 'not-allowed', flexShrink: 0,
          }}
        >
          ⏎ Add
        </button>
      </div>

      {disabled && (
        <div style={{ padding: '0 14px 8px', fontSize: 11, color: '#71717a' }}>
          Attach a browser tab (Drive mode → Record) before using AI Assist.
        </div>
      )}

      {/* Live autocomplete hints — informational, click to insert element name */}
      {!disabled && autocomplete.length > 0 && (state.kind === 'resolving' || state.kind === 'ready') && (
        <div style={{ padding: '0 14px 6px', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          <span style={{ fontSize: 10, color: '#52525b', marginRight: 2 }}>on this page:</span>
          {autocomplete.map((name, i) => (
            <span key={i} style={{
              fontSize: 10, color: '#8b8b93', background: '#18181b', border: '1px solid #27272a',
              borderRadius: 4, padding: '1px 6px',
            }}>{name}</span>
          ))}
        </div>
      )}

      {/* ── State-driven feedback row ── */}
      {!disabled && state.kind === 'error' && (
        <div style={{ padding: '4px 14px 10px' }}>
          <div style={{ fontSize: 11, color: '#f59e0b', marginBottom: 4 }}>
            Not recognized yet — did you mean:
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {state.suggestions.map((s, i) => (
              <button key={i} onClick={() => applySuggestion(s)} style={{
                fontSize: 11, background: '#18181b', border: '1px solid #3f3f46', borderRadius: 5,
                padding: '3px 8px', color: '#c4b5fd', cursor: 'pointer',
              }}>{s}</button>
            ))}
          </div>
        </div>
      )}

      {!disabled && state.kind === 'resolving' && (
        <div style={{ padding: '2px 14px 10px', fontSize: 11, color: '#71717a' }}>
          🔎 Resolving element on the page…
        </div>
      )}

      {!disabled && state.kind === 'no-match' && (
        <div style={{ padding: '4px 14px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#f59e0b' }}>
              {state.reason === 'tree-unavailable'
                ? 'Could not read the page — is a tab attached and the debugger connected?'
                : state.reason === 'model-unavailable'
                ? `Exact/contains match failed for "${state.intent.target}", and the local AI model couldn't load (first use needs one-time internet access to fetch it) — so no smarter match was tried.`
                : `No element found matching "${state.intent.target}".`}
            </span>
            <button onClick={handlePickForNoMatch} style={pickBtnStyle}>🎯 Pick element</button>
          </div>
          {pickError && (
            <div style={{ fontSize: 11, color: '#ef4444', marginTop: 5 }}>{pickError}</div>
          )}
        </div>
      )}

      {!disabled && state.kind === 'ambiguous' && (
        <div style={{ padding: '4px 14px 10px' }}>
          <div style={{ fontSize: 11, color: '#eab308', marginBottom: 5 }}>
            {state.matches.length} elements match "{state.intent.target}" — pick one:
          </div>
          {state.matches.map((m, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', marginBottom: 4,
              background: '#18181b', border: '1px solid #27272a', borderRadius: 5,
            }}>
              <span style={{
                fontSize: 9, fontWeight: 700, color: '#a78bfa', border: '1px solid #7c3aed44',
                borderRadius: 3, padding: '1px 5px', flexShrink: 0,
              }}>{m.node.role}</span>
              <span style={{ fontSize: 11, color: '#e4e4e7', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.accessibleName}
              </span>
              <button onClick={() => commitAmbiguousChoice(m)} style={{ ...confirmBtnStyle, padding: '3px 8px' }}>✓ Use this</button>
            </div>
          ))}
          <button onClick={() => handlePickForAmbiguous(state.matches[0]!)} style={pickBtnStyle}>🎯 Pick a different element</button>
          {pickError && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 5 }}>{pickError}</div>}
        </div>
      )}

      {!disabled && state.kind === 'ready' && (
        <div style={{ padding: '4px 14px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{
              fontSize: 11, color: '#c4b5fd', background: 'rgba(124,58,237,0.12)',
              border: '1px solid rgba(124,58,237,0.3)', borderRadius: 5, padding: '3px 9px',
            }}>
              {describeIntent(state.intent)}
            </span>
            <button onClick={() => commit()} style={confirmBtnStyle}>✓ Confirm</button>
            {(state.intent.target !== undefined) && (
              <button onClick={handlePickForReady} style={pickBtnStyle}>🎯 Pick element</button>
            )}
            <button onClick={() => { setInput(''); setState({ kind: 'idle' }); setPickError(null); }} style={cancelBtnStyle}>✕</button>
          </div>
          {pickError && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 5 }}>{pickError}</div>}
        </div>
      )}

      {flash && (
        <div style={{ padding: '0 14px 8px', fontSize: 11, color: '#22c55e' }}>{flash}</div>
      )}
    </div>
  );
}

function describeIntent(intent: AssistIntent): string {
  switch (intent.kind) {
    case 'openUrl': return `Open ${intent.url}`;
    case 'wait': return `Wait ${intent.seconds}s`;
    case 'screenshot': return 'Take screenshot';
    case 'assertPageContainsText': return `Assert page contains "${intent.text}"`;
    case 'typeText': return intent.target ? `Type "${intent.text}" into ${intent.target}` : `Type "${intent.text}"`;
    case 'tap': return `Tap ${intent.target}`;
    case 'doubleTap': return `Double-tap ${intent.target}`;
    case 'longPress': return `Long-press ${intent.target}`;
    case 'clearText': return `Clear ${intent.target}`;
    case 'waitForVisible': return `Wait for ${intent.target} to be visible`;
    case 'scrollUntilVisible': return `Scroll until ${intent.target} is visible`;
    case 'assertVisible': return `Assert ${intent.target} is visible`;
    case 'assertNotVisible': return `Assert ${intent.target} is hidden`;
    case 'assertEnabled': return `Assert ${intent.target} is enabled`;
    case 'assertDisabled': return `Assert ${intent.target} is disabled`;
    case 'assertHasText': return `Assert ${intent.target} has text "${intent.text}"`;
    default: return intent.raw;
  }
}

const confirmBtnStyle: React.CSSProperties = {
  background: '#7c3aed', border: 'none', borderRadius: 5,
  padding: '4px 10px', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
};

const pickBtnStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #3f3f46', borderRadius: 5,
  padding: '4px 10px', color: '#a1a1aa', fontSize: 11, cursor: 'pointer',
};

const cancelBtnStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #3f3f46', borderRadius: 5,
  padding: '4px 8px', color: '#71717a', fontSize: 11, cursor: 'pointer',
};
